import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MARKETPLACE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { getMarketplaceStores, getMarketplaceProducts } from "../../services/marketplaceApi";
import {
  PREVIEW_MARKETPLACE_STORES,
  PREVIEW_STORE_CATEGORIES,
  filterPreviewStores,
} from "../../data/marketplacePreviewStores";
import {
  MARKETPLACE_FEATURED_BANNER,
  MARKETPLACE_HERO_DEPTH,
  MARKETPLACE_HERO_PRIMARY,
  MARKETPLACE_VAULT_EMPTY_ART,
  categoryMarketplaceThumb,
  previewStoreBannerImage,
  resolveApiStoreBanner,
  resolveProductVisual,
} from "../../data/marketplaceVisualAssets";
import "./marketplace.css";

function CatalogProductVisual({ product }) {
  const url = product.thumbnailUrl || (Array.isArray(product.previewImages) && product.previewImages[0]) || "";
  const src = url || resolveProductVisual(product);
  const isFallback = !url;
  return (
    <>
      <img className="mp-catalog-case__img" src={src} alt="" loading="lazy" />
      {isFallback ? (
        <span className="mp-catalog-case__fallback-tag">Realm art · category match</span>
      ) : null}
    </>
  );
}

function categoryLabelForFilter(filterId) {
  const row = PREVIEW_STORE_CATEGORIES.find((c) => c.id === filterId);
  return row ? row.label : null;
}

function apiStoreMatchesFilter(store, filterId) {
  if (!filterId || filterId === "all") return true;
  const want = categoryLabelForFilter(filterId);
  if (!want) return true;
  const cats = Array.isArray(store.categories) ? store.categories : [];
  return cats.some((c) => String(c).trim().toLowerCase() === want.toLowerCase());
}

export default function MarketplaceHomePage() {
  usePageSeo(MARKETPLACE_SEO);

  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState("");
  const [filterId, setFilterId] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, p] = await Promise.all([getMarketplaceStores(), getMarketplaceProducts()]);
        if (!cancelled) {
          setStores(Array.isArray(s) ? s : []);
          setProducts(Array.isArray(p) ? p : []);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Marketplace unavailable.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const usingPreviewFixtures = stores.length === 0;

  const filteredPreviewStores = useMemo(
    () => filterPreviewStores(PREVIEW_MARKETPLACE_STORES, filterId),
    [filterId]
  );

  const filteredApiStores = useMemo(() => stores.filter((s) => apiStoreMatchesFilter(s, filterId)), [stores, filterId]);

  const storefrontRows = usingPreviewFixtures ? filteredPreviewStores : filteredApiStores;

  const districtStatValue = usingPreviewFixtures ? PREVIEW_MARKETPLACE_STORES.length : stores.length;
  const listingStatValue = products.length;
  const utilityLabel = usingPreviewFixtures ? "Preview districts" : "Live storefronts";

  return (
    <div className="mp-page mp-page--hub mp-atmosphere">
      <section className="mp-hub-hero mp-hub-hero--district">
        <div className="mp-hub-hero__photo-stack" aria-hidden>
          <img className="mp-hub-hero__photo mp-hub-hero__photo--primary" src={MARKETPLACE_HERO_PRIMARY} alt="" />
          <img className="mp-hub-hero__photo mp-hub-hero__photo--depth" src={MARKETPLACE_HERO_DEPTH} alt="" />
          <div className="mp-hub-hero__photo-grad" />
        </div>
        <div className="mp-hub-hero__vignette" aria-hidden />
        <div className="mp-hub-hero__mist" aria-hidden />
        <div className="mp-hub-hero__grid" aria-hidden />
        <div className="mp-hub-hero__rings" aria-hidden />
        <div className="mp-hub-hero__starfield" aria-hidden />
        <div className="mp-hub-hero__scanlines" aria-hidden />
        <div className="mp-hub-hero__content">
          <p className="mp-hero__eyebrow mp-hero__eyebrow--gold">Treydora · Oshar commerce district</p>
          <h1 className="mp-hub-hero__title">Treydora Marketplace</h1>
          <p className="mp-hub-hero__trade-district">Oshar Trade District</p>
          <p className="mp-hub-hero__sub">
            NFT real estate storefronts for digital goods, services, creator tools, lore, and immersive commerce inside
            the Treydora universe.
          </p>
          <p className="mp-hub-hero__support">
            Each storefront represents a future commercial parcel within Oshar — previewing how{" "}
            <strong>Treydora VR Real Estate</strong> can become a living digital economy.
          </p>
          <div className="mp-hub-stats" aria-label="Marketplace preview status">
            <div className="mp-hub-stat mp-glass">
              <span className="mp-hub-stat__value">{districtStatValue}</span>
              <span className="mp-hub-stat__label">{utilityLabel}</span>
            </div>
            <div className="mp-hub-stat mp-glass">
              <span className="mp-hub-stat__value">{listingStatValue}</span>
              <span className="mp-hub-stat__label">Catalog listings</span>
            </div>
            <div className="mp-hub-stat mp-glass">
              <span className="mp-hub-stat__value">Soon</span>
              <span className="mp-hub-stat__label">Seller tools</span>
            </div>
          </div>
          <div className="mp-hub-hero__cta-row">
            <a className="mp-btn mp-btn--primary mp-btn--enabled mp-btn--glow mp-btn--portal" href="#mp-storefronts">
              Explore Storefronts
            </a>
            <Link className="mp-btn mp-btn--ghost mp-btn--enabled mp-btn--outline-gold" to="/dashboard/my-store">
              Real Estate Owner Preview
            </Link>
          </div>
          <p className="mp-hub-hero__fineprint">
            Marketplace purchases, payouts, and seller publishing are <strong>not</strong> enabled in this preview
            phase.
          </p>
        </div>
      </section>

      <section className="mp-featured-bazaar mp-glass" aria-labelledby="mp-featured-bazaar-title">
        <div className="mp-featured-bazaar__visual">
          <img src={MARKETPLACE_FEATURED_BANNER} alt="" className="mp-featured-bazaar__img" loading="lazy" />
          <div className="mp-featured-bazaar__veil" aria-hidden />
        </div>
        <div className="mp-featured-bazaar__copy">
          <p className="mp-hero__eyebrow mp-hero__eyebrow--gold" id="mp-featured-bazaar-title">
            Featured trade corridor
          </p>
          <h2 className="mp-featured-bazaar__title">Digital bazaar · Oshar economy</h2>
          <p className="mp-muted mp-featured-bazaar__lede">
            Own the skyline: NFT real estate unlocks storefront publishing, while the Koraxos comic vault and Treydorex
            access lanes anchor collector rights inside one cinematic trade ring.
          </p>
          <div className="mp-featured-bazaar__cta">
            <a className="mp-btn mp-btn--primary mp-btn--enabled mp-btn--glow mp-btn--portal" href="#mp-storefronts">
              Enter the districts
            </a>
            <Link className="mp-btn mp-btn--ghost mp-btn--enabled mp-btn--outline-gold" to="/world/oshar/real-estate">
              Oshar real estate
            </Link>
          </div>
        </div>
      </section>

      {err ? (
        <p className="mp-banner mp-banner--warn" role="status">
          {err} — preview storefronts remain available below.
        </p>
      ) : null}

      {usingPreviewFixtures ? (
        <div className="mp-preview-ribbon mp-preview-ribbon--elevated" role="status">
          <span className="mp-preview-ribbon__badge">Marketplace Preview</span>
          <span>
            These storefronts are <strong>visual fixtures</strong> for layout and navigation. They are not live seller
            accounts.
          </span>
        </div>
      ) : null}

      <section className="mp-sec mp-sec--filter mp-district-console" id="mp-storefronts">
        <div className="mp-sec__head">
          <h2 className="mp-sec__title mp-sec__title--console">Browse Oshar Districts</h2>
          <p className="mp-sec__lede mp-muted">
            {usingPreviewFixtures
              ? "Filter preview storefronts by the type of commerce each NFT property may support."
              : "Filter live storefronts by category label."}
          </p>
        </div>
        <div className="mp-filter-bar mp-filter-bar--console" role="tablist" aria-label="Store type">
          {PREVIEW_STORE_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={filterId === c.id}
              className={`mp-filter-pill mp-filter-pill--visual ${filterId === c.id ? "mp-filter-pill--active" : ""}`}
              onClick={() => setFilterId(c.id)}
            >
              <span
                className="mp-filter-pill__thumb"
                style={{ backgroundImage: `url(${categoryMarketplaceThumb(c.id)})` }}
                aria-hidden
              />
              <span className="mp-filter-pill__label">{c.label}</span>
            </button>
          ))}
        </div>

        {storefrontRows.length === 0 ? (
          <p className="mp-empty-filter mp-muted">No storefronts in this category yet. Try <strong>All</strong>.</p>
        ) : (
          <div className="mp-grid mp-grid--storefronts">
            {usingPreviewFixtures
              ? storefrontRows.map((s) => (
                  <article
                    key={s.storeSlug}
                    className={`mp-sf-card mp-sf-card--portal mp-glass ${s.heroClass || ""}`}
                  >
                    <div
                      className={`mp-sf-card__banner mp-sf-card__banner--photo mp-sf-card__banner--${s.bannerVariant || "ember"}`}
                    >
                      <img
                        className="mp-sf-card__banner-photo"
                        src={previewStoreBannerImage(s.categoryId)}
                        alt=""
                        loading="lazy"
                      />
                      <div className="mp-sf-card__banner-toning" aria-hidden />
                      <span className="mp-sf-card__banner-icon" aria-hidden>
                        {s.iconLabel || "◆"}
                      </span>
                      <div className="mp-sf-card__banner-shine" aria-hidden />
                    </div>
                    <div className="mp-sf-card__body">
                      <div className="mp-sf-card__badges">
                        <span className="mp-badge mp-badge--district">{s.district}</span>
                        <span className="mp-badge mp-badge--cat">{s.categoryLabel}</span>
                        <span className="mp-badge mp-badge--nft">NFT storefront</span>
                        <span className="mp-badge mp-badge--soon">Coming Soon</span>
                        <span className="mp-badge mp-badge--preview">Preview</span>
                      </div>
                      <h3 className="mp-sf-card__name">{s.storeName}</h3>
                      {s.heroTagline ? <p className="mp-sf-card__tagline">{s.heroTagline}</p> : null}
                      <p className="mp-sf-card__desc">{s.shortDescription}</p>
                      <div className="mp-sf-card__operator">
                        <span className="mp-sf-card__operator-label">Operator</span>
                        <span className="mp-sf-card__operator-name">{s.operatorName}</span>
                      </div>
                      <div className="mp-sf-card__chips" aria-label="Example offerings">
                        {s.offerings.map((o) => (
                          <span key={o} className="mp-offer-chip">
                            {o}
                          </span>
                        ))}
                      </div>
                      <p className="mp-sf-card__nft-line">
                        <span className="mp-sf-card__nft-icon" aria-hidden />
                        Powered by NFT Real Estate
                      </p>
                      <Link
                        className="mp-btn mp-btn--primary mp-btn--enabled mp-sf-card__cta mp-btn--portal"
                        to={`/marketplace/store/${encodeURIComponent(s.storeSlug)}`}
                      >
                        View Store
                      </Link>
                    </div>
                  </article>
                ))
              : storefrontRows.map((s) => (
                  <article key={s._id} className="mp-sf-card mp-sf-card--portal mp-sf-card--live mp-glass">
                    <div className="mp-sf-card__banner mp-sf-card__banner--live mp-sf-card__banner--photo">
                      <img
                        className="mp-sf-card__banner-photo"
                        src={s.bannerUrl || resolveApiStoreBanner(s)}
                        alt=""
                        loading="lazy"
                      />
                      <div className="mp-sf-card__banner-toning" aria-hidden />
                      <span className="mp-sf-card__banner-icon" aria-hidden>
                        ◈
                      </span>
                      <div className="mp-sf-card__banner-shine" aria-hidden />
                    </div>
                    <div className="mp-sf-card__body">
                      <div className="mp-sf-card__badges">
                        <span className="mp-badge mp-badge--live">Live</span>
                        <span className="mp-badge mp-badge--nft">NFT storefront</span>
                      </div>
                      <p className="mp-sf-card__district-pill">
                        {(Array.isArray(s.categories) && s.categories[0]) || "Oshar parcel"}
                      </p>
                      <h3 className="mp-sf-card__name">{s.storeName}</h3>
                      <p className="mp-sf-card__desc">{s.description?.slice(0, 160) || "Digital goods from the Oshar grid."}</p>
                      <p className="mp-sf-card__nft-line">
                        <span className="mp-sf-card__nft-icon" aria-hidden />
                        Powered by NFT Real Estate
                      </p>
                      <Link
                        className="mp-btn mp-btn--primary mp-btn--enabled mp-sf-card__cta mp-btn--portal"
                        to={`/marketplace/store/${encodeURIComponent(s.storeSlug)}`}
                      >
                        View Store
                      </Link>
                    </div>
                  </article>
                ))}
          </div>
        )}
      </section>

      <section className="mp-sec mp-sec--catalog mp-sec--catalog-shell mp-district-console">
        <div className="mp-sec__head">
          <h2 className="mp-sec__title mp-sec__title--console">Catalog listings</h2>
          <p className="mp-muted mp-sec__lede">
            Published products appear here when sellers go live. <strong>Purchases are coming soon</strong> — no checkout
            on Treydora.com in this phase.
          </p>
        </div>
        <div className="mp-grid mp-grid--catalog">
          {products.length === 0 ? (
            <div
              className="mp-catalog-empty mp-glass mp-catalog-empty--art"
              role="status"
              style={{ "--mp-empty-art": `url(${MARKETPLACE_VAULT_EMPTY_ART})` }}
            >
              <span className="mp-catalog-empty__glyph" aria-hidden>
                ◈
              </span>
              <h3 className="mp-catalog-empty__title">Vault row awaiting first listings</h3>
              <p className="mp-muted mp-catalog-empty__copy">
                When sellers publish SKUs through Treydora, each artifact appears here as a sealed trade case — ready to
                inspect, not yet open for purchase.
              </p>
            </div>
          ) : (
            products.map((p) => (
              <Link
                key={p._id}
                className="mp-catalog-case mp-glass"
                to={`/marketplace/product/${encodeURIComponent(p.slug)}${p.storeSlug ? `?storeSlug=${encodeURIComponent(p.storeSlug)}` : ""}`}
              >
                <div className="mp-catalog-case__frame">
                  <CatalogProductVisual product={p} />
                  <span className="mp-catalog-case__badge mp-catalog-case__badge--listing">Listing</span>
                  <span className="mp-catalog-case__badge mp-catalog-case__badge--view">View only</span>
                </div>
                <div className="mp-catalog-case__body">
                  <div className="mp-catalog-case__tags">
                    {p.category ? <span className="mp-chip">{p.category}</span> : <span className="mp-chip">Digital goods</span>}
                    {p.licenseType ? <span className="mp-chip mp-chip--access">{p.licenseType}</span> : null}
                    <span className="mp-chip mp-chip--muted">Oshar catalog</span>
                  </div>
                  <h3 className="mp-catalog-case__title">{p.title}</h3>
                  {p.storeName ? (
                    <p className="mp-catalog-case__seller">
                      <span className="mp-catalog-case__seller-label">Storefront</span> {p.storeName}
                    </p>
                  ) : (
                    <p className="mp-catalog-case__seller mp-muted">
                      <span className="mp-catalog-case__seller-label">Storefront</span> Independent parcel
                    </p>
                  )}
                  <div className="mp-catalog-case__rail">
                    <div>
                      <span className="mp-catalog-case__price-label">Display price</span>
                      <p className="mp-catalog-case__price">${(Number(p.priceCents || 0) / 100).toFixed(2)} USD</p>
                    </div>
                    <span className="mp-catalog-case__cta" aria-hidden>
                      Inspect listing
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
